import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getMerchantProfile, initiateRouteOptimization, listOrdersByMerchant } from "../services/api";
import { useMerchantStore } from "../store/useMerchantStore";
import { getBrowserPickupLocation } from "../utils/merchantPickupLocation";
import { formatMoney } from "../utils/money";
import { orderLineDisplayLabel, orderItemThumbnailSrc } from "../utils/orderLineMedia";
import { ReadyOrdersMap } from "../components/ReadyOrdersMap";
import {
  EmptyState,
  FilterBar,
  PageHeader,
  SearchField,
  StatusPill,
  formatWhen,
  orderMatchesSearch,
  PhoneLink,
} from "../components/orderKit";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

function fmtCreated(iso) {
  return formatWhen(iso);
}

export function ReadyOrders() {
  const { merchantId } = useMerchantStore();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  // `/ready-orders?select=12,15` (from Fulfill / Orders "Plan route") preselects those orders once.
  const [searchParams] = useSearchParams();
  const preselectRef = React.useRef(
    String(searchParams.get("select") || "")
      .split(",")
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0)
  );
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [tourMode, setTourMode] = useState("ONE_WAY");
  // Every order here is READY, so filter by delivery type instead of status.
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  /** @type {{ lat: number, lng: number, label?: string } | null} */
  const [merchantOffice, setMerchantOffice] = useState(null);
  /** Pickup for new routes: the confirmed shop location by default, or this device's position when chosen. */
  const [pickupSource, setPickupSource] = useState("shop");
  const [devicePickup, setDevicePickup] = useState(null);
  const [locating, setLocating] = useState(false);
  const [shopAddress, setShopAddress] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(false);

  const batchableOrderIds = useMemo(
    () => new Set((orders || []).filter((o) => !o.directDeliveryOnly).map((o) => o.id)),
    [orders]
  );

  const refresh = useCallback(async () => {
    setError("");
    setSuccess("");
    if (!merchantId) {
      setOrders([]);
      return;
    }
    setLoading(true);
    try {
      const list = await listOrdersByMerchant(merchantId, "READY");
      list.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
      setOrders(list);
      const wanted = preselectRef.current.filter((id) => list.some((o) => o.id === id));
      preselectRef.current = [];
      if (wanted.length) {
        // Direct-delivery orders are planned alone, so keep just the first one if any is direct.
        const direct = wanted.find((id) => list.find((o) => o.id === id)?.directDeliveryOnly);
        setSelectedIds(new Set(direct ? [direct] : wanted));
      } else {
        setSelectedIds(new Set());
      }
    } catch (err) {
      setError(friendlyError(err, t("ready.load_failed")));
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!merchantId) return undefined;
    let cancelled = false;
    getMerchantProfile(merchantId)
      .then((p) => {
        if (cancelled) return;
        const lat = Number(p?.officeLatitude);
        const lng = Number(p?.officeLongitude);
        if (p?.officeLatitude != null && p?.officeLongitude != null && Number.isFinite(lat) && Number.isFinite(lng)) {
          setMerchantOffice({ lat, lng, label: (p.name && String(p.name).trim()) || t("ready.your_store") });
        } else {
          setMerchantOffice(null);
        }
        setShopAddress(String(p?.officeAddress || "").trim());
      })
      .catch(() => {
        if (!cancelled) setMerchantOffice(null);
      })
      .finally(() => {
        if (!cancelled) setProfileLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  async function useDeviceLocation() {
    setError("");
    setLocating(true);
    try {
      const loc = await getBrowserPickupLocation();
      setDevicePickup(loc);
      setPickupSource("device");
    } catch (e) {
      setError(friendlyError(e, t("location.unknown")));
      setPickupSource("shop");
    } finally {
      setLocating(false);
    }
  }

  const toggleSelect = useCallback(
    (id) => {
      const order = orders.find((o) => o.id === id);
      if (!order) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        if (order.directDeliveryOnly) {
          return new Set([id]);
        }
        for (const x of [...next]) {
          const o = orders.find((y) => y.id === x);
          if (o?.directDeliveryOnly) next.delete(x);
        }
        next.add(id);
        return next;
      });
    },
    [orders]
  );

  const toggleSelectAllBatchable = useCallback(() => {
    if (batchableOrderIds.size === 0) return;
    setSelectedIds((prev) => {
      const allSelected = [...batchableOrderIds].every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(batchableOrderIds);
    });
  }, [batchableOrderIds]);

  function openMap() {
    if (!merchantId || orders.length === 0) return;
    setMapOpen(true);
  }

  async function handlePlanRoute() {
    if (selectedIds.size === 0) {
      setError(t("ready.select_one"));
      return;
    }

    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      const selectedOrders = orders.filter((o) => selectedIds.has(o.id));
      const missingCoord = selectedOrders.filter(
        (o) => o.deliveryLatitude == null || o.deliveryLongitude == null
      );
      if (missingCoord.length > 0) {
        const ids = missingCoord.map((o) => `#${o.id}`).join(", ");
        setError(t("ready.missing_pins", { ids }));
        setSubmitting(false);
        return;
      }

      const hubName = "ERCS";

      // Pickup = confirmed shop location (Store profile), unless the merchant chose this device's position.
      const pickup = pickupSource === "device" && devicePickup ? devicePickup : merchantOffice;
      if (!pickup) {
        setError(t("ready.no_shop_location"));
        setSubmitting(false);
        return;
      }
      const hubLat = pickup.lat;
      const hubLng = pickup.lng;

      let stopsString = `${hubName},${hubLat},${hubLng};`;

      selectedOrders.forEach((order) => {
        if (order.deliveryLatitude != null && order.deliveryLongitude != null) {
          stopsString += `${order.id},${order.deliveryLatitude},${order.deliveryLongitude};`;
        }
      });

      stopsString += `${hubName},${hubLat},${hubLng}`;

      const result = await initiateRouteOptimization(merchantId, {
        merchantId,
        orderIds: Array.from(selectedIds),
        concatenatedStops: stopsString,
        pickupLatitude: hubLat,
        pickupLongitude: hubLng,
        tourMode,
      });

      const routeId = result?.routeId;
      setSuccess(t("ready.optimized", { n: selectedIds.size }));

      if (routeId != null) {
        navigate(`/optimized-route/${routeId}`, {
          state: {
            result,
            selectedOrders,
            merchantId,
            flash: {
              title:
                selectedOrders.length === 1
                  ? t("ready.planned_one", { id: routeId })
                  : t("ready.planned_n", { id: routeId, n: selectedOrders.length }),
              next: t("ready.planned_next"),
            },
          },
        });
      }

      setSelectedIds(new Set());
      await refresh();
    } catch (err) {
      console.error("Route optimization failed:", err);
      setError(friendlyError(err, t("ready.plan_failed")));
    } finally {
      setSubmitting(false);
    }
  }

  const filterOptions = useMemo(() => {
    const direct = orders.filter((o) => o.directDeliveryOnly).length;
    return [
      { id: "all", label: t("common.all"), count: orders.length },
      { id: "standard", label: t("ready.standard"), count: orders.length - direct, dot: "#1f4e79" },
      { id: "direct", label: t("order.direct_delivery"), count: direct, dot: "#b8955a" },
    ];
  }, [orders]);
  const visibleOrders = useMemo(
    () =>
      orders.filter(
        (o) =>
          (filter === "direct" ? o.directDeliveryOnly : filter === "standard" ? !o.directDeliveryOnly : true) &&
          orderMatchesSearch(o, search, orderLineDisplayLabel)
      ),
    [orders, filter, search]
  );

  const selectAllChecked =
    batchableOrderIds.size > 0 && [...batchableOrderIds].every((id) => selectedIds.has(id));

  if (!merchantId) {
    return (
      <div className="page-container">
        <div className="card">
          <p className="muted">{t("ready.sign_in")}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="card">
          <p>{t("ready.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={t("nav.orders")}
        title={t("nav.ready_orders")}
        lead={t("ready.lead")}
        actions={
          <>
            <button type="button" onClick={() => refresh()} disabled={loading}>
              <i className="fa-solid fa-rotate-right" aria-hidden="true" /> {t("common.refresh")}
            </button>
            <button type="button" onClick={openMap} disabled={orders.length === 0}>
              <i className="fa-solid fa-map-location-dot" aria-hidden="true" /> {t("ready.view_on_map")}
            </button>
          </>
        }
      />

      {error && (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      )}
      {success && (
        <div className="alert alert--success">
          <i className="fa-solid fa-circle-check" aria-hidden="true" /> {success}
        </div>
      )}

      <div>
        {orders.length === 0 ? (
          <EmptyState
            icon="fa-box"
            title={t("ready.empty_title")}
            message={t("ready.empty_message")}
          />
        ) : (
          <>
            <div className="list-toolbar">
              <FilterBar options={filterOptions} value={filter} onChange={setFilter} ariaLabel={t("ready.delivery_type")} />
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder={t("orders.search_placeholder")}
                ariaLabel={t("ready.search_aria")}
              />
            </div>

            <p className="muted" style={{ margin: "0 0 14px", fontSize: 12.5 }}>
              <i className="fa-solid fa-circle-info" aria-hidden="true" /> {t("ready.direct_note")}
            </p>

            {visibleOrders.length === 0 ? (
              <EmptyState
                icon="fa-box"
                title={t("common.nothing_here")}
                message={search.trim() ? t("ready.no_search_match", { q: search.trim() }) : t("ready.no_type_match")}
                action={
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("all");
                      setSearch("");
                    }}
                  >
                    {t("ready.show_all")}
                  </button>
                }
              />
            ) : null}

            <div className="fulfill-orders">
              {visibleOrders.map((o) => {
                const checked = selectedIds.has(o.id);
                const isDirect = !!o.directDeliveryOnly;
                const pickId = `ready-order-pick-${o.id}`;
                return (
                  <div
                    key={o.id}
                    className={`card fulfill-order-card rom-ready-order-card${isDirect ? " direct-delivery-order-card" : ""}${
                      checked ? " rom-ready-order-card--selected" : ""
                    }`}
                  >
                    <div className="rom-ready-order-grid">
                      <div className="rom-ready-order-checkbox-cell">
                        <input
                          id={pickId}
                          type="checkbox"
                          className="rom-ready-order-checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(o.id)}
                          aria-labelledby={`ready-order-title-${o.id}`}
                        />
                      </div>
                      <div className="rom-ready-order-header-cell">
                        <label htmlFor={pickId} className="rom-ready-order-header-label">
                          <span id={`ready-order-title-${o.id}`} className="rom-ready-order-title-row">
                            <strong className="rom-ready-order-title-main">
                              {t("order.title", { id: o.id })} · {t("common.amount_etb", { amount: formatMoney(o.total) })}
                            </strong>
                            <StatusPill status={o.status} />
                            {isDirect ? (
                              <span className="rom-direct-chip rom-ready-order-direct-chip">{t("order.direct_delivery")}</span>
                            ) : null}
                          </span>
                          <span className="muted rom-ready-order-created">
                            {fmtCreated(o.createdAt)}
                            {o.clientPhone ? <> · <PhoneLink phone={o.clientPhone} /></> : null}
                          </span>
                        </label>
                      </div>

                      {(o.items || []).length === 0 ? (
                        <p className="muted rom-ready-order-items-empty">{t("order.no_items")}</p>
                      ) : (
                        <ul className="rom-ready-order-items">
                          {(o.items || []).map((item) => (
                            <li key={item.id} className="rom-ready-item-line">
                              <div className="rom-ready-item-thumb-wrap">
                                <img
                                  className="rom-ready-item-thumb"
                                  src={orderItemThumbnailSrc(item.imageUrl)}
                                  alt=""
                                  loading="lazy"
                                />
                              </div>
                              <div className="rom-ready-item-copy">
                                <div className="rom-ready-item-title">
                                  {item.quantity}× {orderLineDisplayLabel(item)}
                                </div>
                                {item.sku && orderLineDisplayLabel(item) !== String(item.sku) ? (
                                  <div className="muted rom-ready-item-sku">{item.sku}</div>
                                ) : null}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="plan-bar plan-bar--bottom">
              <div className="pickup-line">
                <i className="fa-solid fa-store" aria-hidden="true" />
                <span className="pickup-line__text">
                  <strong>{t("ready.pickup")}</strong>{" "}
                  {pickupSource === "device" && devicePickup ? (
                    <>
                      {t("ready.current_location", {
                        coords: `${devicePickup.lat.toFixed(4)}, ${devicePickup.lng.toFixed(4)}`,
                      })}
                    </>
                  ) : merchantOffice ? (
                    <>
                      {merchantOffice.label}
                      {shopAddress ? `- ${shopAddress}` : ""}
                    </>
                  ) : profileLoaded ? (
                    <span className="error">
                      {t("ready.shop_not_set")} <Link to="/store-profile">{t("ready.set_in_profile")}</Link>
                    </span>
                  ) : (
                    t("ready.loading_shop")
                  )}
                </span>
                {pickupSource === "device" ? (
                  <button type="button" className="text-button" onClick={() => setPickupSource("shop")} disabled={!merchantOffice}>
                    {t("ready.use_shop")}
                  </button>
                ) : (
                  <button type="button" className="text-button" onClick={useDeviceLocation} disabled={locating}>
                    {locating ? t("ready.finding_you") : t("ready.use_my_location")}
                  </button>
                )}
              </div>
              <div className="plan-bar__group">
                <div className="segmented" role="radiogroup" aria-label={t("ready.route_type")}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={tourMode === "ONE_WAY"}
                    className={tourMode === "ONE_WAY" ? "segmented__on" : ""}
                    onClick={() => setTourMode("ONE_WAY")}
                  >
                    {t("tour.one_way")}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={tourMode === "ROUND_TRIP"}
                    className={tourMode === "ROUND_TRIP" ? "segmented__on" : ""}
                    onClick={() => setTourMode("ROUND_TRIP")}
                  >
                    {t("tour.round_trip")}
                  </button>
                </div>
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {tourMode === "ONE_WAY" ? t("tour.one_way_hint") : t("tour.round_trip_hint")}
                </span>
              </div>
              <div className="plan-bar__group">
                <button type="button" disabled={batchableOrderIds.size === 0} onClick={toggleSelectAllBatchable}>
                  {selectAllChecked ? t("ready.deselect_all") : t("ready.select_all_standard")}
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={submitting || selectedIds.size === 0}
                  onClick={handlePlanRoute}
                >
                  <i className="fa-solid fa-route" aria-hidden="true" />{" "}
                  {submitting
                    ? t("ready.processing")
                    : selectedIds.size === 0
                      ? t("order.plan_route")
                      : selectedIds.size === 1
                        ? t("ready.plan_route_one")
                        : t("ready.plan_route_n", { n: selectedIds.size })}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {mapOpen ? (
        <ReadyOrdersMap
          orders={orders}
          selectedIds={selectedIds}
          onChangeSelection={setSelectedIds}
          merchantOffice={merchantOffice}
          onClose={() => setMapOpen(false)}
        />
      ) : null}
    </div>
  );
}
